/**
 * Enum for IPA product types.
 *
 * @readonly
 * @enum {number}
 *
 * @property {number} PIPE - Represents piping materials (e.g., heating/cooling pipes).
 * @property {number} DISTRIBUTION - Represents distribution systems (e.g., manifolds, distributors).
 * @property {number} BOX - Represents distribution boxes or housings.
 * @property {number} AUTOMATION - Represents automation/control units.
 * @property {number} TEMPERATURE_CONTROL - Represents temperature control kits or devices.
 * @property {number} DISTRIBUTION_ACCESSORIES - Represents accessories related to distribution systems.
 * @property {number} INSULATION - Represents insulation materials (e.g., thermal or acoustic insulation).
 *
 * @example
 * if (product.type === IPA_PRODUCT_TYPES.PIPE) {
 *   console.log("This product is a pipe.");
 * }
 */

import { PIPE_CHOICES } from "./ProductChoices.js";
export const IPA_PRODUCT_TYPES = {
	PIPE: 1,
	DISTRIBUTION: 2,
	BOX: 3,
	AUTOMATION: 4,
	TEMPERATURE_CONTROL: 5,
	DISTRIBUTION_ACCESSORIES: 6,
	INSULATION: 7,
	ACCESSORY: 8,
	REINFORCEMENT_MESH: 9,
};

export const ACCESSORY_VARIANTS = {
	PERIMETER_BAND: 1,
	ADDITIVE_THERMOFLUID: 2,
	ADDITIVE_ANTI_ALGAE: 3,
	FOAM_TYTAN: 4,
};

export const ACCESSORY_SIZE = {
	DEFAULT: 1,
};

/**
 * Distribution accessories variants
 */
export const DIST_ACCESSORY_VARIANTS = {
	EUROCON: 1, // ex: 312.06.54, 312.09.54, 312.13.54
	CONDUCTIVE_CURVE: 2, // ex: 504.01.14, 504.02.14
	VALVE: 3, // ex: 1304.15.14
	REDUCTION: 4, // ex: 1301.12.54
	MODULE: 5,
	RAPID_CONNECTOR: 6, // ex: 302.10.01, 302.11.01
	VALVE_RR: 7,
	VALVE_TEMP_SEMIOLANDEZ: 8, // 1304.08.56
	VALVE_TEMP_OLANDEZ: 9, // 1304.02.56
};

/**
 * Enum for distribution system variants.
 *
 * @readonly
 * @enum {number}
 *
 
 * @example
 * if (config.distribution === DISTRIBUTION_VARIANTS.MODULAR_RR) {
 *   console.log("Using modular distribution.");
 * }
 */
export const DISTRIBUTION_VARIANTS = {
	METALLIC: 1,
	MODULAR_RR: 2,
	MODULAR_EC: 3,
};

/**
 *  INSULATION Variants
 */
export const INSULATION_VARIANTS = {
	GROOVED: 1,
	TACKER: 2,
	DRY: 3,
	ADDITIONAL_TACKER: 4,
};

/**
 * Grooved panel sizes
 */
export const GROOVED_PANEL_SIZE = {
	DIM_10MM: 1,
	DIM_20MM: 2,
	DIM_30MM: 3,
	DIM_SYSTEM_REDUCED: 4,
};
export const INSULATION_CLIPS = {
	GROOVED_CLIPS: 100,
	TACKER_STAPLES: 200,
};

export const TACKER_PANEL_SIZE = {
	THK_20: 20,
	THK_30: 30,
	THK_40: 40,
	THK_50: 50,
	THK_25: 25,
};

// --- Tacker foil enum (used only when subtype === TACKER) ---
export const TACKER_FOIL = {
	NONE: 0, // "--------"
	SIMPLE: 1, // "Simplă"
	ALUMINIUM: 2, // "Cu aluminiu"
};
export const TACKER_FOIL_ROLL = {
	SIMPLE_110: 10110, // 105.01.05
	SIMPLE_55: 10055, // 105.02.05
	ALU_50: 10050, // 105.03.06
};

/**
 * PIPE Sizes
 */
export const PIPE_SIZE = {
	DIM_16x2_0_240M: 1,
	DIM_16x2_0_500M: 2,
	DIM_17x2_0_500M: 3,
	DIM_17X2_0_240M: 4,
	DIM_20x2_0_400M: 5,
	DIM_16X2_1_500M: 6,
	DIM_17x2_0_600M: 7,
};

export const BOX_MAPPING = {
	SP: 1,
	SISTEMA: 2,
	NU: 3,
};

/**
 * Enum for metal box dimension variants
 */
export const BOX_size = {
	DIM_440x580x110: {
		name: "440x580x110 - SP1",
		width: 440,
		height: 580,
		depth: 110,
	},
	DIM_340x580x110: {
		name: "340x580x110 - SP0",
		width: 340,
		height: 580,
		depth: 110,
	},
	DIM_580x580x110: {
		name: "580x580x110 - SP2",
		width: 580,
		height: 580,
		depth: 110,
	},
	DIM_780x580x110: {
		name: "780x580x110 - SP4",
		width: 780,
		height: 580,
		depth: 110,
	},
	DIM_970x580x110: {
		name: "970x580x110 - SP5",
		width: 970,
		height: 580,
		depth: 110,
	},
	DIM_720x580x110: {
		name: "720x580x110  - SP3",
		width: 720,
		height: 580,
		depth: 110,
	},
	DIM_1145x580x110: {
		name: "1145x580x110  - SP6",
		width: 1145,
		height: 580,
		depth: 110,
	},
	DIM_1345x580x110: {
		name: "1345x580x110  - SP7",
		width: 1345,
		height: 580,
		depth: 110,
	},

	DIM_500x620x110: {
		name: "500x620x110",
		width: 500,
		height: 620,
		depth: 110,
	},
	DIM_600x620x110: {
		name: "600x620x110",
		width: 600,
		height: 620,
		depth: 110,
	},
	DIM_800x620x110: {
		name: "800x620x110",
		width: 800,
		height: 620,
		depth: 110,
	},
	DIM_1000x620x110: {
		name: "1000x620x110",
		width: 1000,
		height: 620,
		depth: 110,
	},
	DIM_1200x620x110: {
		name: "1200x620x110",
		width: 1200,
		height: 620,
		depth: 110,
	},
	DIM_1300x620x110: {
		name: "1300x620x110",
		width: 1300,
		height: 620,
		depth: 110,
	},
};
export const DIST_ACC = {
	// MODULAR_RR
	MOD_ACC_NO_KIT: 101, // 302.05.01
	MOD_ACC_WITH_KIT: 102, // 302.06.01
	MOD_TERMINAL: 103, // 302.07.01
	MOD_MIX_25_60: 110, // 401.02.00
	MOD_MIX_25_70: 111, // 401.03.00
	MOD_CONN_A: 120, // 1303.03.73
	MOD_CONN_B: 121, // 1301.12.54

	// MODULAR_EC
	MON_CONN_WITH_KIT: 210, // 1304.08.56
	MON_MIX_25_60: 211, // 401.05.00
	MON_MIX_25_70: 212, // 401.06.00
	MON_CONN_NO_KIT: 213, // 1303.03.73

	// METALLIC
	MET_CONN: 310, // 1304.02.56
	MET_MIX_25_60: 311, // 401.05.00
	MET_MIX_25_70: 312, // 401.06.00

	// COMUNE (folosite la toate tipurile – vezi note)
	PUMP_COUNTER: 400, // 1401.10.76
};

/**
 * Product codes of thermostats that appear in recommendations for automation 2 (cu fir) or 3 (RF).
 * Used to show a warning when saving/creating an offer that has automation 2 or 3 and contains these recommended thermostats.
 */
export const RECOMMENDED_THERMOSTAT_PRODUCT_CODES = [
	"603.01.18", // Termostat mecanic cu fir
	"603.02.17", // Termostat digital cu fir, HC
	"603.04.16", // Termostat digital cu fir, ZigBee
	"609.01.86", // Termostat Touch cu fir
	"609.02.86", // Termostat Touch cu fir si Wi-Fi
	"604.05.16", // Termostat digital programabil cu RF (alb)
	"604.07.16", // Termostat digital programabil cu RF (negru)
	"604.08.16", // Termostat digital cu RF, ZigBee, HC, cu baterii (alb)
];

/**
 * List of IPA products available in the system.
 *
 * @type {IPAProduct[]}
 */
export const ipa_products = [
	/**
	 * DISTRIBUTION
	 */
	{
		id: 1,
		product_code: "304.02.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (2 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 2,
		},
		requirements: [],
	},
	{
		id: 2,
		product_code: "304.03.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (3 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 3,
		},
		requirements: [],
	},
	{
		id: 3,
		product_code: "304.04.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (4 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 4,
		},
		requirements: [],
	},
	{
		id: 4,
		product_code: "304.05.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (5 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 5,
		},
		requirements: [],
	},
	{
		id: 5,
		product_code: "304.06.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (6 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 6,
		},
		requirements: [],
	},
	{
		id: 6,
		product_code: "304.07.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (7 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 7,
		},
		requirements: [],
	},
	{
		id: 7,
		product_code: "304.08.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (8 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 8,
		},
		requirements: [],
	},
	{
		id: 7777,
		product_code: "304.09.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (9 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 9,
		},
		requirements: [],
	},
	{
		id: 8,
		product_code: "304.10.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (10 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 10,
		},
		requirements: [],
	},
	{
		id: 9,
		product_code: "304.11.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (11 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 11,
		},
		requirements: [],
	},
	{
		id: 10,
		product_code: "304.12.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (12 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 12,
		},
		requirements: [],
	},
	{
		id: 11,
		product_code: "304.13.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (13 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 13,
		},
		requirements: [],
	},
	{
		id: 12,
		product_code: "304.14.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (14 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 14,
		},
		requirements: [],
	},
	{
		id: 1234,
		product_code: "304.15.00",
		name: 'Distribuitor-Colector metalic 1"x3/4", cu debitmetre (15 circuite)',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: 15,
		},
		requirements: [],
	},
	{
		id: 13,
		product_code: "316.02.54",
		name: "Distribuitor-colector modular, cu eurocon (2 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 2,
		},
		requirements: [],
	},
	{
		id: 14,
		product_code: "316.03.54",
		name: "Distribuitor-colector modular, cu eurocon (3 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 3,
		},
		requirements: [],
	},
	{
		id: 15,
		product_code: "316.04.54",
		name: "Distribuitor-colector modular, cu eurocon (4 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 4,
		},
		requirements: [],
	},
	{
		id: 16,
		product_code: "316.05.54",
		name: "Distribuitor-colector modular, cu eurocon (5 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 5,
		},
		requirements: [],
	},
	{
		id: 17,
		product_code: "316.06.54",
		name: "Distribuitor-colector modular, cu eurocon (6 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 6,
		},
		requirements: [],
	},
	{
		id: 18,
		product_code: "316.07.54",
		name: "Distribuitor-colector modular, cu eurocon (7 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 7,
		},
		requirements: [],
	},
	{
		id: 19,
		product_code: "316.08.54",
		name: "Distribuitor-colector modular, cu eurocon (8 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 8,
		},
		requirements: [],
	},
	{
		id: 20,
		product_code: "316.09.54",
		name: "Distribuitor-colector modular, cu eurocon (9 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 9,
		},
		requirements: [],
	},
	{
		id: 21,
		product_code: "316.10.54",
		name: "Distribuitor-colector modular, cu eurocon (10 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 10,
		},
		requirements: [],
	},
	{
		id: 22,
		product_code: "316.11.54",
		name: "Distribuitor-colector modular, cu eurocon (11 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 11,
		},
		requirements: [],
	},
	{
		id: 23,
		product_code: "316.12.54",
		name: "Distribuitor-colector modular, cu eurocon (12 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 12,
		},
		requirements: [],
	},
	{
		id: 24,
		product_code: "316.13.54",
		name: "Distribuitor-colector modular, cu eurocon (13 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 13,
		},
		requirements: [],
	},
	{
		id: 25,
		product_code: "316.14.54",
		name: "Distribuitor-colector modular, cu eurocon (14 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 14,
		},
		requirements: [],
	},
	{
		id: 26,
		product_code: "316.15.54",
		name: "Distribuitor-colector modular, cu eurocon (15 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 15,
		},
		requirements: [],
	},
	{
		id: 26.1,
		product_code: "316.16.54",
		name: "Distribuitor-colector modular, cu eurocon (16 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: 16,
		},
		requirements: [],
	},
	{
		id: 26.2,
		product_code: "301.02.00",
		name: "Distribuitor-colector modular (2 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 2,
		},
		requirements: [],
	},
	{
		id: 27,
		product_code: "301.03.00",
		name: "Distribuitor-colector modular (3 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 3,
		},
		requirements: [],
	},
	{
		id: 28,
		product_code: "301.04.00",
		name: "Distribuitor-colector modular (4 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 4,
		},
		requirements: [],
	},
	{
		id: 29,
		product_code: "301.05.00",
		name: "Distribuitor-colector modular (5 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 5,
		},
		requirements: [],
	},
	{
		id: 30,
		product_code: "301.06.00",
		name: "Distribuitor-colector modular (6 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 6,
		},
		requirements: [],
	},
	{
		id: 31,
		product_code: "301.07.00",
		name: "Distribuitor-colector modular (7 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 7,
		},
		requirements: [],
	},
	{
		id: 32,
		product_code: "301.08.00",
		name: "Distribuitor-colector modular (8 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 8,
		},
		requirements: [],
	},
	{
		id: 33,
		product_code: "301.09.00",
		name: "Distribuitor-colector modular (9 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 9,
		},
		requirements: [],
	},
	{
		id: 34,
		product_code: "301.10.00",
		name: "Distribuitor-colector modular (10 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 10,
		},
		requirements: [],
	},
	{
		id: 35,
		product_code: "301.11.00",
		name: "Distribuitor-colector modular (11 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 11,
		},
		requirements: [],
	},
	{
		id: 36,
		product_code: "301.12.00",
		name: "Distribuitor-colector modular (12 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 12,
		},
		requirements: [],
	},
	{
		id: 37,
		product_code: "301.13.00",
		name: "Distribuitor-colector modular (13 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 13,
		},
		requirements: [],
	},
	{
		id: 38,
		product_code: "301.14.00",
		name: "Distribuitor-colector modular (14 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 14,
		},
		requirements: [],
	},
	{
		id: 39,
		product_code: "301.15.00",
		name: "Distribuitor-colector modular (15 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 15,
		},
		requirements: [],
	},
	{
		id: 40,
		product_code: "301.16.00",
		name: "Distribuitor-colector modular (16 circuite)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: 16,
		},
		requirements: [],
	},

	/**
	 * BOX
	 */
	{
		id: 1001,
		product_code: "505.02.09",
		name: "Cutie metalica 600x620x110",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SISTEMA,
			size: BOX_size.DIM_600x620x110,
		},
		requirements: [],
	},
	{
		id: 1002,
		product_code: "505.03.09",
		name: "Cutie metalica 800x620x110",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SISTEMA,
			size: BOX_size.DIM_800x620x110,
		},
		requirements: [],
	},
	{
		id: 1003,
		product_code: "505.04.09",
		name: "Cutie metalica 1000x620x110",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SISTEMA,
			size: BOX_size.DIM_1000x620x110,
		},
		requirements: [],
	},
	{
		id: 1004,
		product_code: "505.05.09",
		name: "Cutie metalica 1200x620x110",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SISTEMA,
			size: BOX_size.DIM_1200x620x110,
		},
		requirements: [],
	},
	{
		id: 1005,
		product_code: "505.06.09",
		name: "Cutie metalica 1300x620x110",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SISTEMA,
			size: BOX_size.DIM_1300x620x110,
		},
		requirements: [],
	},
	{
		id: 1006,
		product_code: "505.10.44",
		name: "Cutie metalica Cutie metalica 720x580x110 - SP3",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SP,
			size: BOX_size.DIM_720x580x110,
		},
		requirements: [],
	},
	{
		id: 1007,
		product_code: "505.08.44",
		name: "Cutie metalica 440x580x110 - SP1",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SP,
			size: BOX_size.DIM_440x580x110,
		},
		requirements: [],
	},
	{
		id: 1008,
		product_code: "505.09.44",
		name: "Cutie metalica 580x580x110 - SP2",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SP,
			size: BOX_size.DIM_580x580x110,
		},
		requirements: [],
	},
	{
		id: 1009,
		product_code: "505.11.44",
		name: "Cutie metalica 780x580x110 - SP4",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SP,
			size: BOX_size.DIM_780x580x110,
		},
		requirements: [],
	},
	{
		id: 1010,
		product_code: "505.12.44",
		name: "Cutie metalica 970x580x110 - SP5",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SP,
			size: BOX_size.DIM_970x580x110,
		},
		requirements: [],
	},
	{
		id: 1011,
		product_code: "505.07.44",
		name: "Cutie metalica 340x580x110 - SP0",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SP,
			size: BOX_size.DIM_340x580x110,
		},
		requirements: [],
	},
	{
		id: 1012,
		product_code: "505.13.44",
		name: "Cutie metalic 1145x580x110 - SP6",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SP,
			size: BOX_size.DIM_1145x580x110,
		},
		requirements: [],
	},
	{
		id: 1013,
		product_code: "505.14.44",
		name: "Cutie metalică 1345x580x110 - SP7",
		specifications: {
			type: IPA_PRODUCT_TYPES.BOX,
			subtype: BOX_MAPPING.SP,
			size: BOX_size.DIM_1345x580x110,
		},
		requirements: [],
	},

	/**
	 * DISTRIBUTION ACCESSORIES (detailed by type)
	 */

	{
		id: 6001,
		product_code: "312.06.54",
		name: "Eurocon 16x2,03/4'' Pex",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.EUROCON,
			size: 16,
		},
		requirements: [],
	},
	{
		id: 6002,
		product_code: "312.09.54",
		name: "Eurocon 17x2,03/4'' Pex",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.EUROCON,
			size: 17,
		},
		requirements: [],
	},
	{
		id: 6003,
		product_code: "312.13.54",
		name: "Eurocon 20x2,03/4'' universal",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.EUROCON,
			size: 20,
		},
		requirements: [],
	},
	{
		id: 6004,
		product_code: "504.01.14",
		name: "Curbe conductoare 16–18",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.CONDUCTIVE_CURVE,
			size: 16,
		},
		requirements: [],
	},
	{
		id: 6005,
		product_code: "504.02.14",
		name: "Curbe conductoare 20–22",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.CONDUCTIVE_CURVE,
			size: 20,
		},
		requirements: [],
	},
	{
		id: 6006,
		product_code: "1304.11.54",
		name: "Robinet distribuitor drept cu olandez 1",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.VALVE,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 6007,
		product_code: "1301.12.56",
		name: "Reducție alamă 1''/4–1''",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.REDUCTION,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 6008,
		product_code: "1303.03.73",
		name: "Robinet drept 1''",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.VALVE_RR,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 6009,
		product_code: "302.05.01",
		name: "Modul accesorii (aerisitor automat, robinet golire, termometru)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.MODULE,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 6010,
		product_code: "302.06.01",
		name: "Modul accesorii cu kit temperatură (modular)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.MODULE,
			size: 2,
		},
		requirements: [],
	},
	{
		id: 6011,
		product_code: "302.07.01",
		name: "Modul terminal",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.MODULE,
			size: 3,
		},
		requirements: [],
	},
	{
		id: 6015,
		product_code: "317.05.54",
		name: "Modul accesorii cu aerisitor automat",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.MODULE,
			size: 4,
		},
		requirements: [],
	},
	{
		id: 6012,
		product_code: "302.10.01",
		name: "Racord rapid Pex 16",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.RAPID_CONNECTOR,
			size: 16,
		},
		requirements: [],
	},
	{
		id: 6013,
		product_code: "302.11.01",
		name: "Racord rapid Pex 17",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.RAPID_CONNECTOR,
			size: 17,
		},
		requirements: [],
	},
	{
		id: 6014,
		product_code: "302.12.01",
		name: "Racord rapid Pex 20",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.RAPID_CONNECTOR,
			size: 20,
		},
		requirements: [],
	},
	/**
	 * INSULATION GROOVED
	 */

	/**
	 * INSULATION GROOVED - 102 (use only when is on stock from warehouse)
	 */
	// {
	// 	id: 1015,
	// 	product_code: "101.01.102",
	// 	name: "Panou cu nuturi termoformat h.11",
	// 	specifications: {
	// 		type: IPA_PRODUCT_TYPES.INSULATION,
	// 		subtype: INSULATION_VARIANTS.GROOVED,
	// 		size: GROOVED_PANEL_SIZE.DIM_10MM,
	// 	},
	// 	requirements: [],
	// },
	// {
	// 	id: 1016,
	// 	product_code: "101.02.102",
	// 	name: "Panou cu nuturi termoformat h.20",
	// 	specifications: {
	// 		type: IPA_PRODUCT_TYPES.INSULATION,
	// 		subtype: INSULATION_VARIANTS.GROOVED,
	// 		size: GROOVED_PANEL_SIZE.DIM_20MM,
	// 	},
	// 	requirements: [],
	// },
	// {
	// 	id: 1017,
	// 	product_code: "101.03.102",
	// 	name: "Panou cu nuturi termoformat h.30",
	// 	specifications: {
	// 		type: IPA_PRODUCT_TYPES.INSULATION,
	// 		subtype: INSULATION_VARIANTS.GROOVED,
	// 		size: GROOVED_PANEL_SIZE.DIM_30MM,
	// 	},
	// 	requirements: [],
	// },

	/**
	 * INSULATION GROOVED - 51 (use only when *102 is not on stock from warehouse)
	 */
	{
		id: 1015,
		product_code: "101.01.51",
		name: "Panou cu nuturi termoformat h.10",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.GROOVED,
			size: GROOVED_PANEL_SIZE.DIM_10MM,
		},
		requirements: [],
	},
	{
		id: 1016,
		product_code: "101.02.51",
		name: "Panou cu nuturi termoformat h.20",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.GROOVED,
			size: GROOVED_PANEL_SIZE.DIM_20MM,
		},
		requirements: [],
	},
	{
		id: 1017,
		product_code: "101.03.51",
		name: "Panou cu nuturi termoformat h.30",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.GROOVED,
			size: GROOVED_PANEL_SIZE.DIM_30MM,
		},
		requirements: [],
	},

	
	{
		id: 1018,
		product_code: "102.01.02",
		name: "Panou cu nuturi pentru sistem redus h.5",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.GROOVED,
			size: GROOVED_PANEL_SIZE.DIM_SYSTEM_REDUCED,
		},
		requirements: [],
	},
	{
		id: 1019,
		product_code: "106.03.14",
		name: "Clips pentru panou cu nuturi",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.GROOVED,
			size: INSULATION_CLIPS.GROOVED_CLIPS,
		},
		requirements: [],
	},
	/**
	 * INSULATION DRY
	 */
	{
		id: 1020,
		product_code: "102.03.06",
		name: "Panou EPS cu aluminiu pentru sistem uscat",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.DRY,
			size: 1,
		},
		requirements: [],
	},
	/**
	 * INSULATION TACKER
	 */
	{
		id: 2104,
		product_code: "103.01.51",
		name: "Rolă Tacker 25mm",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.TACKER,
			size: TACKER_PANEL_SIZE.THK_25,
		},
		requirements: [],
	},
	{
		id: 2105,
		product_code: "104.02.51",
		name: "Placa tacker 20mm",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.TACKER,
			size: TACKER_PANEL_SIZE.THK_20,
		},
		requirements: [],
	},
	{
		id: 2106,
		product_code: "104.03.51",
		name: "Placa tacker 30mm",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.TACKER,
			size: TACKER_PANEL_SIZE.THK_30,
		},
		requirements: [],
	},
	{
		id: 2107,
		product_code: "104.05.51",
		name: "Placa tacker 50mm",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.TACKER,
			size: TACKER_PANEL_SIZE.THK_50,
		},
		requirements: [],
	},
	{
		id: 2125,
		product_code: "104.02.51",
		name: "Placa tacker 20mm",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.ADDITIONAL_TACKER,
			size: TACKER_PANEL_SIZE.THK_20,
		},
		requirements: [],
	},
	{
		id: 2126,
		product_code: "104.03.51",
		name: "Placa tacker 30mm",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.ADDITIONAL_TACKER,
			size: TACKER_PANEL_SIZE.THK_30,
		},
		requirements: [],
	},
	{
		id: 2127,
		product_code: "104.05.51",
		name: "Placa tacker 50mm",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.ADDITIONAL_TACKER,
			size: TACKER_PANEL_SIZE.THK_50,
		},
		requirements: [],
	},
	{
		id: 2108,
		product_code: "106.02.06",
		name: "Clips Tacker 40mm",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.TACKER,
			size: INSULATION_CLIPS.TACKER_STAPLES,
		},
		requirements: [],
	},
	{
		id: 2201,
		product_code: "105.01.05",
		name: "Folie Tacker 110 m²",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.TACKER,
			size: TACKER_FOIL_ROLL.SIMPLE_110,
		},
		requirements: [],
	},
	{
		id: 2202,
		product_code: "105.02.05",
		name: "Folie Tacker 55 m²",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.TACKER,
			size: TACKER_FOIL_ROLL.SIMPLE_55,
		},
		requirements: [],
	},
	{
		id: 2203,
		product_code: "105.03.06",
		name: "Folie Tacker cu aluminiu 50 m²",
		specifications: {
			type: IPA_PRODUCT_TYPES.INSULATION,
			subtype: INSULATION_VARIANTS.TACKER,
			size: TACKER_FOIL_ROLL.ALU_50,
		},
		requirements: [],
	},
	{
		id: 3001,
		product_code: "302.05.01",
		name: "Modul accesorii (aerisitor automat, robinet golire, termometru)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: DIST_ACC.MOD_ACC_NO_KIT,
		},
		requirements: [],
	},
	{
		id: 3002,
		product_code: "302.06.01",
		name: "Modul termometru",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: DIST_ACC.MOD_ACC_WITH_KIT,
		},
		requirements: [],
	},
	{
		id: 3003,
		product_code: "302.07.01",
		name: "Modul terminal",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: DIST_ACC.MOD_TERMINAL,
		},
		requirements: [],
	},

	{
		id: 3004,
		product_code: "401.02.00",
		name: "Kit reglare temperatură 25-60 – modular",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: DIST_ACC.MOD_MIX_25_60,
		},
		requirements: [],
	},
	{
		id: 3005,
		product_code: "401.03.00",
		name: "Kit reglare temperatură 25-70 – modular",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: DIST_ACC.MOD_MIX_25_70,
		},
		requirements: [],
	},
	{
		id: 3006,
		product_code: "1303.03.73",
		name: "Accesorii conexiune A (modular) — (ex. set racorduri)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: DIST_ACC.MOD_CONN_A,
		},
		requirements: [],
	},
	{
		id: 3007,
		product_code: "1301.12.56",
		name: "Accesorii conexiune B (modular) — (ex. piese montaj)",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_RR,
			size: DIST_ACC.MOD_CONN_B,
		},
		requirements: [],
	},

	// DISTRIBUTION ACCESSORIES — MODULAR_EC
	{
		id: 3101,
		product_code: "1304.08.56",
		name: "Accesorii conexiune (cu kit) – monolitic",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: DIST_ACC.MON_CONN_WITH_KIT,
		},
		requirements: [],
	},
	{
		id: 3102,
		product_code: "401.05.00",
		name: "Kit reglare temperatură 25-60 – monolitic",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: DIST_ACC.MON_MIX_25_60,
		},
		requirements: [],
	},
	{
		id: 3103,
		product_code: "401.06.00",
		name: "Kit reglare temperatură 25-70 – monolitic",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: DIST_ACC.MON_MIX_25_70,
		},
		requirements: [],
	},
	{
		id: 3104,
		product_code: "1303.03.73",
		name: "Accesorii conexiune (fără kit) – monolitic",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.MODULAR_EC,
			size: DIST_ACC.MON_CONN_NO_KIT,
		},
		requirements: [],
	},

	// DISTRIBUTION ACCESSORIES — METALLIC
	{
		id: 3201,
		product_code: "1304.02.56",
		name: "Accesorii conexiune – metalic",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: DIST_ACC.MET_CONN,
		},
		requirements: [],
	},
	{
		id: 3202,
		product_code: "401.05.00",
		name: "Kit reglare temperatură 25-60 – metalic",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: DIST_ACC.MET_MIX_25_60,
		},
		requirements: [],
	},
	{
		id: 3203,
		product_code: "401.06.00",
		name: "Kit reglare temperatură 25-70 – metalic",
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DISTRIBUTION_VARIANTS.METALLIC,
			size: DIST_ACC.MET_MIX_25_70,
		},
		requirements: [],
	},

	/**
	 * PIPE
	 */

	{
		id: 4001,
		product_code: "201.03.04",
		name: "Teavă PE-Xa 16x2,0mm EVOH (500 m)",
		specifications: {
			type: IPA_PRODUCT_TYPES.PIPE,
			subtype: PIPE_CHOICES.find(p => p.value === 16).value,
			size: PIPE_SIZE.DIM_16x2_0_500M,
		},
		requirements: [],
	},
	{
		id: 4002,
		product_code: "201.05.04",
		name: "Teavă PE-Xa 17x2,0mm EVOH (500 m)",
		specifications: {
			type: IPA_PRODUCT_TYPES.PIPE,
			subtype: PIPE_CHOICES.find(p => p.value === 17).value,
			size: PIPE_SIZE.DIM_17x2_0_500M,
		},
		requirements: [],
	},
	{
		id: 4007,
		product_code: "201.05.03",
		name: "Teavă PE-Xa 17x2,0mm EVOH (600 m)",
		specifications: {
			type: IPA_PRODUCT_TYPES.PIPE,
			subtype: PIPE_CHOICES.find(p => p.value === 117).value,
			size: PIPE_SIZE.DIM_17x2_0_600M,
		},
		requirements: [],
	},
	{
		id: 4003,
		product_code: "201.06.04",
		name: "Teavă PE-Xa 20x2,0mm EVOH (400 m)",
		specifications: {
			type: IPA_PRODUCT_TYPES.PIPE,
			subtype: PIPE_CHOICES.find(p => p.value === 20).value,
			size: PIPE_SIZE.DIM_20x2_0_400M,
		},
		requirements: [],
	},
	{
		id: 4004,
		product_code: "201.17.04",
		name: "Teavă PE-RT 16x2,0mm EVOH (500 m)",
		specifications: {
			type: IPA_PRODUCT_TYPES.PIPE,
			subtype: PIPE_CHOICES.find(p => p.value === 116).value,
			size: PIPE_SIZE.DIM_16X2_1_500M,
		},
		requirements: [],
	},
	{
		id: 4005,
		product_code: "201.02.04",
		name: "Teava PE-Xa 16x2,0mm EVOH (240 m)",
		specifications: {
			type: IPA_PRODUCT_TYPES.PIPE,
			subtype: PIPE_CHOICES.find(p => p.value === 16).value,
			size: PIPE_SIZE.DIM_16x2_0_240M,
		},
		requirements: [],
	},
	{
		id: 4006,
		product_code: "201.04.04",
		name: "Teava PE-Xa 17x2,0mm EVOH (240 m)",
		specifications: {
			type: IPA_PRODUCT_TYPES.PIPE,
			subtype: PIPE_CHOICES.find(p => p.value === 17).value,
			size: PIPE_SIZE.DIM_17X2_0_240M,
		},
		requirements: [],
	},

	/**
	 * ACCESSORIES
	 */
	{
		id: 5001,
		product_code: "501.01.10",
		name: "Bandă perimetrală complet adezivă, cu folie",
		specifications: {
			type: IPA_PRODUCT_TYPES.ACCESSORY,
			subtype: ACCESSORY_VARIANTS.PERIMETER_BAND,
			size: ACCESSORY_SIZE.DEFAULT,
		},
		requirements: [],
	},
	{
		id: 5002,
		product_code: "502.01.11",
		name: "Aditiv termofluidizant pentru șapă 10 lt.",
		specifications: {
			type: IPA_PRODUCT_TYPES.ACCESSORY,
			subtype: ACCESSORY_VARIANTS.ADDITIVE_THERMOFLUID,
			size: ACCESSORY_SIZE.DEFAULT,
		},
		requirements: [],
	},
	{
		id: 5003,
		product_code: "502.05.12",
		name: "Aditiv antialge și protecție",
		specifications: {
			type: IPA_PRODUCT_TYPES.ACCESSORY,
			subtype: ACCESSORY_VARIANTS.ADDITIVE_ANTI_ALGAE,
			size: ACCESSORY_SIZE.DEFAULT,
		},
		requirements: [],
	},
	{
		id: 5004,
		product_code: "1401.10.76",
		name: "Spumă Tytan pentru polistiren",
		specifications: {
			type: IPA_PRODUCT_TYPES.ACCESSORY,
			subtype: ACCESSORY_VARIANTS.FOAM_TYTAN,
			size: ACCESSORY_SIZE.DEFAULT,
		},
		requirements: [],
	},

	// --- TEMPERATURE CONTROL PRODUCTS ---

	{
		id: 7000,
		product_code: "401.05.00",
		name: "Kit reglare temperatura metalic, cu pompa 25-60 clasa A",
		specifications: {
			type: IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL,
			subtype: 2,
			size: 1,
		},
		requirements: [],
	},

	{
		id: 7001,
		product_code: "401.06.00",
		name: "Kit reglare temperatura metalic, cu pompa 25-70 clasa A",
		specifications: {
			type: IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL,
			subtype: 3,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 7002,
		product_code: "401.07.00",
		name: "Kit reglare temperatură 3 cai, cu pompa 25-80 130",
		specifications: {
			type: IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL,
			subtype: 4,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 7003,
		product_code: "401.08.00",
		name: "Kit reglare temperatură 4 cai, cu pompa 25-60 130",
		specifications: {
			type: IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL,
			subtype: 5,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 7004,
		product_code: "401.09.00",
		name: "Kit reglare temperatură 4 cai, cu pompa 25-70 130",
		specifications: {
			type: IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL,
			subtype: 6,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 7005,
		product_code: "401.10.00",
		name: "Kit reglare temperatură 4 cai, cu pompa 25-80 130",
		specifications: {
			type: IPA_PRODUCT_TYPES.TEMPERATURE_CONTROL,
			subtype: 7,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 7006,
		product_code: "1304.08.56",
		name: 'Robinet drept semiolandez 1"-3/4"',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.VALVE_TEMP_SEMIOLANDEZ,
			size: 1,
		},
		requirements: [],
	},
	{
		id: 7007,
		product_code: "1304.02.56",
		name: 'Robinet drept cu olandez 3/4"',
		specifications: {
			type: IPA_PRODUCT_TYPES.DISTRIBUTION_ACCESSORIES,
			subtype: DIST_ACCESSORY_VARIANTS.VALVE_TEMP_OLANDEZ,
			size: 1,
		},
		requirements: [],
	},
	// ============================================================================
	// AUTOMATION PRODUCTS
	// ============================================================================

	// --- COMUNE (servomotoare, cutii, senzori etc.) ---
	{
		id: 8000,
		product_code: "601.01.54",
		name: "Servomotor 230V - normal închis",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 1, // comun
			size: 1,
		},
		requirements: [],
	},

	{
		id: 8050,
		product_code: "604.06.16",
		name: "Termostat digital programabil cu receptor si Wi-Fi, HC, cu baterie (alb)",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 1,
			size: 4,
		},
		requirements: [],
	},
	{
		id: 8016,
		product_code: "605.11.16",
		name: "Releu inteligent ZigBee",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 1,
			size: 7,
		},
		requirements: [],
	},
	{
		id: 8017,
		product_code: "605.12.16",
		name: "Kit releu fara fir controlat prin radio",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 1,
			size: 5,
		},
		requirements: [],
	},
	{
		id: 8018,
		product_code: "609.09.86",
		name: "Senzor pardoseală ",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 1,
			size: 6,
		},
		requirements: [],
	},
	{
		id: 8007,
		product_code: "605.10.16",
		name: "Gateway PRO Zigbee",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 1,
			size: 3,
		},
		requirements: [],
	},
	{
		id: 8011,
		product_code: "602.08.00",
		name: "Releu 2 contacte NC/NO 8A",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 1,
			size: 8,
		},
		requirements: [],
	},

	// --- DA, CU FIR ---
	{
		id: 8001,
		product_code: "602.10.54",
		name: "Cutie de comanda 8 zone, 230V",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 2,
			size: 6,
		},
		requirements: [
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2, // cu fir
				size: 1,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2,
				size: 2,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2,
				size: 3,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2,
				size: 4,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2,
				size: 5,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 4,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 7,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 5,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 6,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 8,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 3,
				quantity: 0,
			},
		],
		recommendations: [
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2, // cu fir
				size: 1,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2, // cu fir
				size: 2,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2, // cu fir
				size: 3,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2, // cu fir
				size: 4,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 2, // cu fir
				size: 5,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 4,
			},
			{ type: IPA_PRODUCT_TYPES.AUTOMATION, subtype: 1, size: 7 },
			{ type: IPA_PRODUCT_TYPES.AUTOMATION, subtype: 1, size: 5 },
			{ type: IPA_PRODUCT_TYPES.AUTOMATION, subtype: 1, size: 6 },
			{ type: IPA_PRODUCT_TYPES.AUTOMATION, subtype: 1, size: 8 },
			{ type: IPA_PRODUCT_TYPES.AUTOMATION, subtype: 1, size: 3 },
		],
	},
	{
		id: 8002,
		product_code: "603.01.18",
		name: "Termostat mecanic cu fir, 230V (alb)",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 2, // cu fir
			size: 1,
		},
		requirements: [],
	},
	{
		id: 8003,
		product_code: "603.02.17",
		name: "Termostat digital cu fir, HC, cu baterii (alb)",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 2,
			size: 2,
		},
		requirements: [],
	},
	{
		id: 8004,
		product_code: "603.04.16",
		name: "Termostat digital cu fir, ZigBee, HC, 230V (alb)",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 2,
			size: 3,
		},
		requirements: [],
	},
	{
		id: 8005,
		product_code: "609.01.86",
		name: "Termostat Touch cu fir, montaj in doza, 230V (alb)",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 2,
			size: 4,
		},
		requirements: [],
	},
	{
		id: 8006,
		product_code: "609.02.86",
		name: "Termostat Touch cu fir si Wi-Fi, montaj in doza, 230V (alb)",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 2,
			size: 5,
		},
		requirements: [],
	},

	// --- DA, CU RF ---
	{
		id: 8012,
		product_code: "602.11.16",
		name: "Cutie de comandă 6 zone ZigBee",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 3, // RF
			size: 1,
		},
		requirements: [
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 3, // RF
				size: 2,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 3, // RF
				size: 3,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 4,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 7,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 5,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 6,
				quantity: 0,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 8,
				quantity: 0,
			},
		],
		recommendations: [
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 3, // RF
				size: 2,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 3, // RF
				size: 3,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 3, // RF
				size: 4,
			},
			{
				type: IPA_PRODUCT_TYPES.AUTOMATION,
				subtype: 1,
				size: 4,
			},
			{ type: IPA_PRODUCT_TYPES.AUTOMATION, subtype: 1, size: 7 },
			{ type: IPA_PRODUCT_TYPES.AUTOMATION, subtype: 1, size: 5 },
			{ type: IPA_PRODUCT_TYPES.AUTOMATION, subtype: 1, size: 6 },
			{ type: IPA_PRODUCT_TYPES.AUTOMATION, subtype: 1, size: 8 },
		],
	},
	{
		id: 8020,
		product_code: "604.05.16",
		name: "Termostat digital programabil cu RF si RH, Zigbee, cu baterie Li-Ion (alb)",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 3,
			size: 2,
		},
		requirements: [],
	},
	{
		id: 8013,
		product_code: "604.07.16",
		name: "Termostat digital programabil cu RF si RH, Zigbee, cu baterie Li-Ion (negru)",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 3,
			size: 3,
		},
		requirements: [],
	},
	{
		id: 8021,
		product_code: "604.08.16",
		name: "Termostat digital cu RF, ZigBee, HC, cu baterii (alb)",
		specifications: {
			type: IPA_PRODUCT_TYPES.AUTOMATION,
			subtype: 3,
			size: 4,
		},
		requirements: [],
	},
	{
		id: 9000,
		product_code: "503.01.13",
		name: "Plasa fibra pentru armarea sapei",
		specifications: {
			type: IPA_PRODUCT_TYPES.REINFORCEMENT_MESH,
			subtype: 1,
			size: 50,
		},

		requirements: [],
	},
];
